/**
 * WritingModesContext — estado global de los modos de escritura.
 *
 * Tres modos, exclusivos o combinables:
 *   • focusMode      — atenúa el panel lateral y las secciones no activas.
 *   • typewriterMode — mantiene el cursor centrado verticalmente al escribir.
 *   • zenMode        — pantalla completa sin distracciones; Esc para salir.
 *
 * focusMode y typewriterMode se persisten en localStorage (clave: wrlens:modes).
 * zenMode NO se persiste — arrancar en pantalla completa total sería confuso.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const MODES_STORAGE_KEY = 'wrlens:modes'
const DEFAULT_TYPEWRITER_ANCHOR_RATIO = 0.5

function clampTypewriterAnchorRatio(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return DEFAULT_TYPEWRITER_ANCHOR_RATIO
  return Math.min(0.75, Math.max(0.25, numeric))
}

function readStoredModes(): { focusMode: boolean; typewriterMode: boolean; typewriterAnchorRatio: number } {
  try {
    const raw = localStorage.getItem(MODES_STORAGE_KEY)
    if (!raw) return { focusMode: false, typewriterMode: false, typewriterAnchorRatio: DEFAULT_TYPEWRITER_ANCHOR_RATIO }
    const parsed = JSON.parse(raw)
    return {
      focusMode: Boolean(parsed?.focusMode),
      typewriterMode: Boolean(parsed?.typewriterMode),
      typewriterAnchorRatio: clampTypewriterAnchorRatio(parsed?.typewriterAnchorRatio),
    }
  } catch {
    return { focusMode: false, typewriterMode: false, typewriterAnchorRatio: DEFAULT_TYPEWRITER_ANCHOR_RATIO }
  }
}

interface WritingModesState {
  focusMode: boolean
  typewriterMode: boolean
  typewriterAnchorRatio: number
  zenMode: boolean
  setFocusMode: (v: boolean) => void
  setTypewriterMode: (v: boolean) => void
  setTypewriterAnchorRatio: (v: number) => void
  setZenMode: (v: boolean) => void
  enterFocusMode: () => void
  exitFocusMode: () => void
  enterZenMode: () => void
  exitZenMode: () => void
  toggleFocusMode: () => void
  toggleTypewriterMode: () => void
  toggleZenMode: () => void
}

const WritingModesContext = createContext<WritingModesState | null>(null)

export function WritingModesProvider({ children }: { children: React.ReactNode }) {
  const [focusMode, setFocusMode] = useState(() => readStoredModes().focusMode)
  const [typewriterMode, setTypewriterMode] = useState(() => readStoredModes().typewriterMode)
  const [typewriterAnchorRatio, setTypewriterAnchorRatioState] = useState(
    () => readStoredModes().typewriterAnchorRatio,
  )
  const [zenMode, setZenMode] = useState(false)
  const setTypewriterAnchorRatio = useCallback((nextValue: number) => {
    setTypewriterAnchorRatioState(clampTypewriterAnchorRatio(nextValue))
  }, [])

  const enterFocusMode = useCallback(() => {
    setFocusMode(true)
    setZenMode(false)
  }, [])
  const exitFocusMode = useCallback(() => setFocusMode(false), [])
  const enterZenMode = useCallback(() => {
    setZenMode(true)
    setFocusMode(false)
  }, [])
  const exitZenMode = useCallback(() => setZenMode(false), [])
  const toggleFocusMode = useCallback(() => {
    if (zenMode) {
      enterFocusMode()
      return
    }
    setFocusMode(v => !v)
  }, [enterFocusMode, zenMode])
  const toggleTypewriterMode = useCallback(() => setTypewriterMode(v => !v), [])
  const toggleZenMode = useCallback(() => {
    if (focusMode) {
      enterZenMode()
      return
    }
    setZenMode(v => !v)
  }, [enterZenMode, focusMode])

  // Persistir focusMode y typewriterMode en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(MODES_STORAGE_KEY, JSON.stringify({
        focusMode,
        typewriterMode,
        typewriterAnchorRatio,
      }))
    } catch { /* SSR / safari private */ }
  }, [focusMode, typewriterMode, typewriterAnchorRatio])

  // Zen mode: Esc para salir
  useEffect(() => {
    if (!zenMode) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitZenMode()
    }
    window.addEventListener('keydown', handler, { capture: true })
    return () => window.removeEventListener('keydown', handler, { capture: true })
  }, [exitZenMode, zenMode])

  // Atajos de teclado globales para los modos (RFC-202509-010)
  // ⌘⇧F → Foco   ⌘⇧M → Máquina   ⌘⇧Z → Zen
  // - e.code en vez de e.key para no depender del layout del teclado
  // - capture:true para interceptar antes del handler de redo/browser
  // - ⌘⇧T se evita: Safari lo captura a nivel chrome (reabrir pestaña)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return
      if (e.code === 'KeyF') { e.preventDefault(); toggleFocusMode() }
      if (e.code === 'KeyM') { e.preventDefault(); setTypewriterMode(v => !v) }
      if (e.code === 'KeyZ') { e.preventDefault(); toggleZenMode() }
    }
    window.addEventListener('keydown', handler, { capture: true })
    return () => window.removeEventListener('keydown', handler, { capture: true })
  }, [toggleFocusMode, toggleZenMode])

  // Zen mode: evitar scroll del body y añadir clase para hooks de CSS externos
  useEffect(() => {
    if (zenMode) {
      document.documentElement.classList.add('wrlens-zen')
      document.body.style.overflow = 'hidden'
    } else {
      document.documentElement.classList.remove('wrlens-zen')
      document.body.style.overflow = ''
    }
    return () => {
      document.documentElement.classList.remove('wrlens-zen')
      document.body.style.overflow = ''
    }
  }, [zenMode])

  const ctxValue = useMemo(() => ({
    focusMode,
    typewriterMode,
    typewriterAnchorRatio,
    zenMode,
    setFocusMode,
    setTypewriterMode,
    setTypewriterAnchorRatio,
    setZenMode,
    enterFocusMode,
    exitFocusMode,
    enterZenMode,
    exitZenMode,
    toggleFocusMode,
    toggleTypewriterMode,
    toggleZenMode,
  }), [
    enterFocusMode,
    enterZenMode,
    exitFocusMode,
    exitZenMode,
    focusMode,
    typewriterMode,
    typewriterAnchorRatio,
    zenMode,
    setFocusMode,
    setTypewriterMode,
    setTypewriterAnchorRatio,
    setZenMode,
    toggleFocusMode,
    toggleTypewriterMode,
    toggleZenMode,
  ])

  return (
    <WritingModesContext.Provider value={ctxValue}>
      {children}
    </WritingModesContext.Provider>
  )
}

export function useWritingModesCtx(): WritingModesState {
  const ctx = useContext(WritingModesContext)
  if (!ctx) throw new Error('useWritingModesCtx must be used inside <WritingModesProvider>')
  return ctx
}
